import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { LimitCardGrid } from "@/features/subventions/components/feed/LimitCardGrid";
import { ReferenceList } from "@/features/subventions/components/feed/ReferenceList";
import { SectionHeading } from "@/features/subventions/components/feed/SectionHeading";
import { UniversePills } from "@/features/subventions/components/feed/UniversePills";
import type { EligibleSubsidy } from "@/features/subventions/types/subvention.types";
import { limitCards } from "@/features/subventions/utils/subventionStatus";

type SubventionInlineDetailProps = {
  subsidy: EligibleSubsidy
  onOpenDetail: (subsidy: EligibleSubsidy) => void
};

const DIVIDER_CLASS = "border-t border-gray-200 dark:border-gray-700";
const INLINE_REFERENCE_CAP = 3;

export const SubventionInlineDetail = ({ subsidy, onOpenDetail }: SubventionInlineDetailProps) => {
  const references = subsidy.references ?? [];
  const shownReferences = references.slice(0, INLINE_REFERENCE_CAP);
  const hiddenReferenceCount = references.length - shownReferences.length;
  const hasLimits = limitCards(subsidy).length > 0;
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
      animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="px-4 pb-4 pl-[73px]"
    >
      {references.length > 0 && (
        <div className="py-3.5">
          <SectionHeading as="h4">Comment c'est calculé</SectionHeading>
          <ReferenceList references={shownReferences} variant="inline" />
          {hiddenReferenceCount > 0 && (
            <button
              type="button"
              onClick={() => onOpenDetail(subsidy)}
              className="mt-2.5 inline-flex items-center gap-1 text-[13px] font-bold text-primary hover:text-primary-emphasis"
            >
              {`+ ${hiddenReferenceCount} autres enseignes`}
              <ArrowRight size={16} aria-hidden />
            </button>
          )}
        </div>
      )}

      {hasLimits && (
        <div className={`py-3.5 ${DIVIDER_CLASS}`}>
          <SectionHeading as="h4">Limites par période</SectionHeading>
          <LimitCardGrid subsidy={subsidy} />
        </div>
      )}

      <div className={`py-3.5 ${DIVIDER_CLASS}`}>
        <SectionHeading as="h4">Où l'utiliser</SectionHeading>
        <UniversePills subsidy={subsidy} />
      </div>

      <div className={`flex justify-end pt-3.5 ${DIVIDER_CLASS}`}>
        <button
          type="button"
          onClick={() => onOpenDetail(subsidy)}
          className="inline-flex items-center gap-1 text-[13px] font-bold text-primary hover:text-primary-emphasis"
        >
          Voir le détail complet
          <ArrowRight size={16} aria-hidden />
        </button>
      </div>
    </motion.div>
  );
};

SubventionInlineDetail.displayName = "SubventionInlineDetail";
